import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { adminAPI } from '../services/api';
import { useToast } from '../context/ToastContext';

interface UserActionsModalProps {
  open: boolean;
  onClose: () => void;
  user: any;
  action: 'role' | 'deactivate';
  onSuccess: () => void;
}

const UserActionsModal: React.FC<UserActionsModalProps> = ({
  open,
  onClose,
  user,
  action,
  onSuccess,
}) => {
  const [selectedRole, setSelectedRole] = useState(user?.role || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { showSuccess, showError } = useToast();

  const handleRoleChange = async () => {
    if (!selectedRole || selectedRole === user.role) {
      onClose();
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      await adminAPI.updateUserRole(user.id, selectedRole);
      showSuccess(`Rolle von ${user.name} erfolgreich geändert`);
      onSuccess();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Fehler beim Ändern der Benutzerrolle';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const isDeactivated = user?.role === 'deactivated';

  const handleDeactivate = async () => {
    try {
      setIsLoading(true);
      setError(null);
      if (isDeactivated) {
        const restoreRole = user.original_role || 'customer';
        await adminAPI.updateUserRole(user.id, restoreRole);
        showSuccess(`Benutzer ${user.name} aktiviert`);
      } else {
        await adminAPI.deactivateUser(user.id);
        showSuccess(`Benutzer ${user.name} deaktiviert`);
      }
      onSuccess();
    } catch (err: any) {
      const message = err.response?.data?.message || 'Fehler beim Ändern des Benutzerstatus';
      setError(message);
      showError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirm = () => {
    if (action === 'role') {
      handleRoleChange();
    } else {
      handleDeactivate();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {action === 'role'
          ? user?.role === 'pending' ? 'Dienstleister freischalten' : 'Benutzerrolle ändern'
          : isDeactivated ? 'Benutzer aktivieren' : 'Benutzer deaktivieren'}
      </DialogTitle>
      <DialogContent>
        <Box mt={2}>
          <Typography mb={2}>
            <strong>Benutzer:</strong> {user?.name} ({user?.email})
          </Typography>

          {action === 'role' ? (
            <>
              {user?.role === 'pending' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Dieser Dienstleister wartet auf Freischaltung. Rolle auf <strong>Dienstleister</strong> setzen, um das Konto zu aktivieren.
                </Alert>
              )}
              <FormControl fullWidth>
                <InputLabel>Neue Rolle</InputLabel>
                <Select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  label="Neue Rolle"
                >
                  {user?.role === 'pending' && <MenuItem value="pending">⏳ Ausstehend</MenuItem>}
                  <MenuItem value="contractor">Dienstleister</MenuItem>
                  <MenuItem value="customer">Kunde</MenuItem>
                  <MenuItem value="staff">Mitarbeiter</MenuItem>
                  <MenuItem value="admin">Administrator</MenuItem>
                </Select>
              </FormControl>
            </>
          ) : (
            <Alert severity={isDeactivated ? 'info' : 'warning'}>
              {isDeactivated
                ? 'Das Benutzerkonto wird wieder aktiviert. Der Benutzer kann sich danach erneut anmelden.'
                : 'Das Benutzerkonto wird deaktiviert. Der Benutzer kann sich dann nicht mehr anmelden.'}
            </Alert>
          )}

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Abbrechen
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={action === 'role' ? 'primary' : isDeactivated ? 'success' : 'error'}
          disabled={isLoading || (action === 'role' && selectedRole === user?.role)}
        >
          {isLoading ? 'Wird verarbeitet...' : 'Bestätigen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserActionsModal;
